import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";

export default function ScholarshipApplicationsPage() {
  const { scholarshipId } = useParams();
  const navigate = useNavigate();
  const isGlobal = !scholarshipId;
  const [apps, setApps] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [scholarshipName, setScholarshipName] = useState("");
  const [scholarships, setScholarships] = useState([]);
  const [scholarshipFilter, setScholarshipFilter] = useState("");

  useEffect(() => {
    if (!isGlobal) return;
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    client.get("/admin/scholarships")
      .then(res => setScholarships(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = (p = 1) => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    setLoading(true);
    client.get("/admin/applications", {
      params: { scholarship_id: scholarshipId || scholarshipFilter || undefined, search, sort, page: p },
    })
      .then(res => {
        setApps(res.data.data);
        setMeta(res.data);
        setPage(p);
        if (!isGlobal && res.data.data[0]?.scholarship?.title) {
          setScholarshipName(res.data.data[0].scholarship.title);
        }
      })
      .catch(() => toast.error("Failed to load applications."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [search, sort, scholarshipFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("nd-rest-tkn");
      if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
      const res = await client.get("/admin/applications/export", {
        params: { scholarship_id: scholarshipId || scholarshipFilter || undefined, sort },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `applications-${scholarshipId || scholarshipFilter || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => navigate("/dashboard/admin/scholarships")}
          className="text-sm text-gray-400 hover:text-gray-700 transition-colors">← Scholarships</button>
      </div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {isGlobal ? "All Applications" : (scholarshipName ? `${scholarshipName} — Applications` : "Scholarship Applications")}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{meta?.total != null ? `${meta.total} paid applications` : "Paid applications only"}</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isGlobal && (
            <select value={scholarshipFilter} onChange={e => setScholarshipFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
              <option value="">All scholarships</option>
              {scholarships.map(s => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          )}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-52" />
          <select value={sort} onChange={e => setSort(e.target.value)}
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
            <option value="desc">Latest paid first</option>
            <option value="asc">Oldest paid first</option>
          </select>
          <button onClick={exportCsv} disabled={exporting}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50">
            {exporting ? "Exporting…" : "⬇ Export CSV"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : apps.length === 0 ? (
          <div className="p-16 text-center"><p className="text-4xl mb-3">📋</p><p className="text-sm font-medium text-gray-600">No paid applications found</p></div>
        ) : (
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Scholarship", "Name", "Email", "Phone", "Course", "Type", "Amount", "Provider", "Paid At"].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {apps.map(a => (
                <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-700 text-xs font-medium max-w-[160px] truncate">{a.scholarship?.title || "—"}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">{a.name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{a.email}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{a.phone || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-[140px] truncate">{a.selected_course || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-[140px] truncate">{a.selected_scholarship_type || "—"}</td>
                  <td className="px-4 py-3 font-semibold text-gray-800 text-xs">
                    {a.payment_amount ? `₦${Number(a.payment_amount).toLocaleString()}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{a.payment_provider || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {a.paid_at ? new Date(a.paid_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50">Next →</button>
        </div>
      )}
    </div>
  );
}
